import { notification } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiSubscriptionSubscribe } from '../../../api/subscription';
import { formatPrice } from '../../../utils/format';
import { searchPostalCode } from '../../../utils/searchPostalCode';
import { extractPhoneBackendErrors, validateCardNumber, validateCEP, validateEmail, validateName, validatePhoneForCountry, validateTaxpayerID } from '../../../utils/validatesCheckout';
import CountryCodeSelect, { DEFAULT_COUNTRY_CODE } from '../CountryCodeSelect/CountryCodeSelect';
import ExpiryDateSelector from '../ExpiryDateSelector/ExpiryDateSelector';
import InputCheckout from '../InputCheckout/InputCheckout';
import PhoneInputCheckout from '../PhoneInputCheckout/PhoneInputCheckout';
import StateSelect from '../StateSelect/StateSelect';
import './SubscriptionCheckoutForm.scss';

const SubscriptionCheckoutForm = ({ planInfo }) => {
  const [paymentType, setPaymentType] = useState('');

  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasPopulatedFromURL = useRef(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCountryCode: DEFAULT_COUNTRY_CODE,
    taxpayerId: '',
    ccNumber: '',
    ccName: '',
    ccCvv: '',
    addressCep: '',
    addressLine1: '',
    addressNumber: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressComplement: '',
  });

  const [errors, setErrors] = useState({});
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const [returnPix, setReturnPix] = useState(null);
  const [returnBoleto, setReturnBoleto] = useState(null);
  const [errorPurchase, setErrorPurchase] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [validFields, setValidFields] = useState({});
  const [selectedMonth, setSelectedMonth] = useState('01');
  const [selectedYear, setSelectedYear] = useState('25');
  const [postalCodeData, setPostalCodeData] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const validate = useCallback(() => {
    const newErrors = {};

    const nameError = validateName(formData?.customerName);
    if (nameError) {
      newErrors.customerName = nameError;
    }

    const emailError = validateEmail(formData?.customerEmail);
    if (emailError) {
      newErrors.customerEmail = emailError;
    }

    const phoneError = validatePhoneForCountry(formData?.customerPhone, formData?.customerCountryCode);
    if (phoneError) {
      newErrors.customerPhone = phoneError;
    }

    if (paymentType === 'credit_card') {
      const taxpayerError = validateTaxpayerID(formData?.taxpayerId);
      if (taxpayerError) {
        newErrors.taxpayerId = taxpayerError;
      }

      const ccNameError = validateName(formData?.ccName);
      if (ccNameError) {
        newErrors.ccName = ccNameError;
      }

      if (!formData.ccCvv) {
        newErrors.ccCvv = 'Esse campo é obrigatório';
      }

      const ccNumberError = validateCardNumber(formData?.ccNumber);
      if (ccNumberError) {
        newErrors.ccNumber = ccNumberError;
      }
    }

    if (paymentType === 'boleto') {
      const taxpayerError = validateTaxpayerID(formData?.taxpayerId);
      if (taxpayerError) {
        newErrors.taxpayerId = taxpayerError;
      }

      const cepError = validateCEP(formData?.addressCep?.replace(/-/g, ''));
      if (cepError) {
        newErrors.addressCep = cepError;
      }

      if (!formData.addressLine1) newErrors.addressLine1 = 'Esse campo é obrigatório';
      if (!formData.addressNumber) newErrors.addressNumber = 'Esse campo é obrigatório';
      if (!formData.addressNeighborhood) newErrors.addressNeighborhood = 'Esse campo é obrigatório';
      if (!formData.addressCity) newErrors.addressCity = 'Esse campo é obrigatório';
      if (!formData.addressState) newErrors.addressState = 'Esse campo é obrigatório';
    }

    if (paymentType === 'pix') {
      const taxpayerError = validateTaxpayerID(formData?.taxpayerId);
      if (taxpayerError) {
        newErrors.taxpayerId = taxpayerError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, paymentType]);

  const handleSubmit = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }
    if (!paymentType) {
      notification.warning({
        message: "Atenção",
        description: "Selecione uma forma de pagamento",
        placement: "topRight",
      });
      return;
    }
    if (validate()) {
      setIsDisabled(true);
      setLoading(true);

      let payload = {
        name: formData.customerName,
        email: formData.customerEmail,
        phone_number: formData.customerPhone,
        country_code: formData.customerCountryCode,
        type: paymentType,
      };

      if (paymentType === 'credit_card') {
        payload = {
          ...payload,
          taxpayer_id: formData.taxpayerId,
          installments: 1,
          card: {
            number: formData.ccNumber.replace(/\s/g, ''),
            cvv: formData.ccCvv,
            name: formData.ccName,
            expirationMonth: selectedMonth,
            expirationYear: `20${selectedYear}`,
          },
          save_card: saveCard,
        };
      }

      if (paymentType === 'boleto') {
        payload = {
          ...payload,
          taxpayer_id: formData.taxpayerId,
          address: {
            line1: formData.addressLine1,
            line2: formData.addressNumber,
            neighborhood: formData.addressNeighborhood,
            city: formData.addressCity,
            state: formData.addressState,
            postalCode: formData.addressCep?.replace(/-/g, ''),
          },
        };
      }

      if (paymentType === 'pix') {
        payload = {
          ...payload,
          taxpayer_id: formData.taxpayerId,
        };
      }

      apiSubscriptionSubscribe(planInfo.id, payload)
        .then((data) => {
          setLoading(false);

          if (data?.httpStatus === 402) {
            setErrorDetails(data?.error || { message_display: data?.message || 'Cartão recusado' });
            setErrorPurchase(true);
            return;
          }

          if (data?.httpStatus === 400) {
            const { phone, countryCode } = extractPhoneBackendErrors(data);
            if (phone || countryCode) {
              if (countryCode) {
                console.warn('Backend rejected country_code:', countryCode);
              }
              setErrors((prev) => ({
                ...prev,
                ...(phone ? { customerPhone: phone } : {}),
                ...(countryCode ? { customerCountryCode: countryCode } : {}),
              }));
              setValidFields((prev) => ({
                ...prev,
                ...(phone ? { customerPhone: false } : {}),
                ...(countryCode ? { customerCountryCode: false } : {}),
              }));
              setIsDisabled(false);
              return;
            }
          }

          if (data?.httpStatus >= 400) {
            setErrorDetails(data?.errors || data?.error || { message_display: data?.message || 'Erro ao processar assinatura' });
            setErrorPurchase(true);
            return;
          }

          if (data?.payment?.pix) {
            setReturnPix(data);
            return;
          }

          if (data?.payment?.boleto) {
            setReturnBoleto(data);
            return;
          }

          setSubscriptionSuccess(data);
          notification.success({
            message: "Sucesso",
            description: "Assinatura realizada com sucesso!",
            placement: "topRight",
          });
        })
        .catch(() => {
          setErrorDetails(null);
          setErrorPurchase(true);
          setLoading(false);
        });
    }
  }, [validate, formData, planInfo, paymentType, selectedMonth, selectedYear, saveCard]);

  const validateField = useCallback((name, value) => {
    let error = '';
    let isValid = true;

    if (name === 'customerName' || name === 'ccName') {
      error = validateName(value);
      isValid = error === '';
    }

    if (name === 'customerPhone') {
      error = validatePhoneForCountry(value, formData.customerCountryCode);
      isValid = error === '';
    }

    if (name === 'customerEmail') {
      error = validateEmail(value);
      isValid = error === '';
    }

    if (name === 'taxpayerId') {
      error = validateTaxpayerID(value);
      isValid = error === '';
    }

    if (name === 'ccNumber') {
      error = validateCardNumber(value);
      isValid = error === '';
    }

    if (name === 'addressCep') {
      const rawCep = value?.replace(/-/g, '');
      error = validateCEP(rawCep);
      if (error === '') {
        setPostalCodeData(null);
        searchPostalCode(rawCep, setPostalCodeData);
      }
      isValid = error === '';
    }

    if (value?.trim() === '' && !error) {
      error = 'Este campo é obrigatório';
      isValid = false;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
    setValidFields((prevValid) => ({
      ...prevValid,
      [name]: isValid,
    }));
  }, [formData.customerCountryCode]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const nextValue = name === 'ccNumber' ? value.replace(/\D/g, '') : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: nextValue,
    }));

    if (name === 'customerCountryCode') {
      setErrors((prev) => ({ ...prev, customerCountryCode: '' }));
      if (formData.customerPhone) {
        const error = validatePhoneForCountry(formData.customerPhone, value);
        const isValid = error === '';
        setErrors((prev) => ({ ...prev, customerPhone: error }));
        setValidFields((prev) => ({ ...prev, customerPhone: isValid }));
      }
      return;
    }

    validateField(name, nextValue);
  }, [validateField, formData.customerPhone]);

  useEffect(() => {
    if (hasPopulatedFromURL.current) return;

    const urlParams = new URLSearchParams(window.location.search);

    const nameParam = urlParams.get('name');
    const emailParam = urlParams.get('email');
    const phoneParam = urlParams.get('phone');

    // Boleto fields
    const taxpayerParam = urlParams.get('taxpayer');
    const cepParam = urlParams.get('cep');
    const addressParam = urlParams.get('address');
    const numberParam = urlParams.get('number');
    const complementParam = urlParams.get('complement');
    const neighborhoodParam = urlParams.get('neighborhood');
    const cityParam = urlParams.get('city');
    const stateParam = urlParams.get('state');

    const updates = {};

    if (nameParam) {
      updates.customerName = nameParam;
    }
    if (emailParam) {
      updates.customerEmail = emailParam;
    }
    if (phoneParam) {
      updates.customerPhone = phoneParam;
    }

    // Boleto field updates
    if (taxpayerParam) {
      updates.taxpayerId = taxpayerParam;
    }
    if (cepParam) {
      updates.addressCep = cepParam;
    }
    if (addressParam) {
      updates.addressLine1 = addressParam;
    }
    if (numberParam) {
      updates.addressNumber = numberParam;
    }
    if (complementParam) {
      updates.addressComplement = complementParam;
    }
    if (neighborhoodParam) {
      updates.addressNeighborhood = neighborhoodParam;
    }
    if (cityParam) {
      updates.addressCity = cityParam;
    }
    if (stateParam) {
      updates.addressState = stateParam;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prevData => ({
        ...prevData,
        ...updates
      }));

      setTimeout(() => {
        Object.entries(updates).forEach(([field, value]) => {
          validateField(field, value);
        });
      }, 0);

      hasPopulatedFromURL.current = true;
    }
  }, [validateField]);

  useEffect(() => {
    if (postalCodeData && !postalCodeData?.erro) {
      setFormData(prevData => ({
        ...prevData,
        addressLine1: postalCodeData?.logradouro || prevData.addressLine1,
        addressCity: postalCodeData?.localidade || prevData.addressCity,
        addressNeighborhood: postalCodeData?.bairro || prevData.addressNeighborhood,
        addressState: postalCodeData?.uf || prevData.addressState,
        addressCep: postalCodeData?.cep || prevData.addressCep,
      }));
      setValidFields(prev => ({
        ...prev,
        addressLine1: true,
        addressCity: true,
        addressNeighborhood: true,
        addressState: true,
      }));
    }
  }, [postalCodeData]);

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(returnPix?.payment?.pix?.copy_paste);
      notification.success({
        message: "Sucesso",
        description: "Código copiado com sucesso!",
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: "Erro",
        description: "Falha ao copiar o código.",
        placement: "topRight",
      });
    }
  };

  function CircleAlertIcon(props) {
    return (
      <svg
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="8" y2="12" />
        <line x1="12" x2="12.01" y1="16" y2="16" />
      </svg>
    );
  }

  function OrderUnsuccessful({ errorDetails }) {
    const formatErrors = (errors) => {
      if (typeof errors === 'string') return errors;
      if (errors?.message_display) return errors.message_display;
      if (errors?.message) return errors.message;
      if (typeof errors === 'object') {
        return Object.entries(errors).map(([key, value]) => (
          <p key={key} className="error-message">{Array.isArray(value) ? value.join(', ') : value}</p>
        ));
      }
      return 'Erro desconhecido';
    };

    return (
      <div className="OrderUnsuccessful">
        <div className="content">
          <CircleAlertIcon className="icon" />
          <h1 className="title">Assinatura malsucedida</h1>
          {errorDetails && (
            <div className="error-details">
              {formatErrors(errorDetails)}
            </div>
          )}
          <p className="message">
            Lamentamos, mas houve um problema ao processar sua assinatura. Tente novamente ou entre em contato com o suporte ao cliente se o problema persistir.
          </p>
          <div className="actions">
            <button onClick={() => window.location.reload()} className="primary">Tentar novamente</button>
            <button className="secondary">Suporte</button>
          </div>
        </div>
      </div>
    );
  }

  const SubscriptionSuccess = () => {
    const subscriberId = subscriptionSuccess?.subscriber?.id;
    const savedCard = subscriptionSuccess?.subscriber?.saved_card;
    const cancelUrl = subscriberId ? `/assinar/${planInfo?.id}/${subscriberId}/cancelar` : null;

    return (
      <div className="SubscriptionSuccess">
        <div className="content">
          <CircleCheckIcon className="icon" />
          <h1 className="title">Assinatura realizada!</h1>
          <p className="message">
            Obrigado por assinar. Sua assinatura foi ativada com sucesso.
          </p>
          <div className="order-info">
            <div className="plan-name">{planInfo?.name}</div>
            <div className="total">{formatPrice(planInfo?.amount)}</div>
          </div>
          {savedCard && (
            <div className="saved-card-info">
              <CreditCardIcon className="saved-card-icon" />
              <div className="saved-card-details">
                <span className="saved-card-brand">{savedCard.card_brand}</span>
                <span className="saved-card-number">**** {savedCard.last4_digits}</span>
                <span className="saved-card-holder">{savedCard.holder_name}</span>
              </div>
              <p className="saved-card-message">
                Suas próximas cobranças serão realizadas automaticamente neste cartão.
              </p>
            </div>
          )}
          {cancelUrl && (
            <div className="manage-subscription">
              <p className="manage-info">
                Guarde este link para gerenciar sua assinatura:
              </p>
              <a href={cancelUrl} className="manage-link">
                Gerenciar Assinatura
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const CircleCheckIcon = (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );

  const CreditCardIcon = (props) => (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
  );

  const showForm = !errorPurchase && !subscriptionSuccess && !returnPix && !returnBoleto;

  return (
    <>
      {showForm && (
        <div className="SubscriptionCheckoutForm">
          <h2>Seus dados</h2>
          <form id="subscription-checkout-form" onSubmit={handleSubmit}>
            <div className="container-data">
              <div className="row">
                <InputCheckout
                  disabled={isDisabled}
                  label="Nome"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  errorMessage={errors.customerName}
                  isValid={validFields.customerName}
                />
              </div>
              <div className="row">
                <InputCheckout
                  disabled={isDisabled}
                  label="E-mail"
                  name="customerEmail"
                  type='email'
                  className="small"
                  value={formData.customerEmail}
                  onChange={handleChange}
                  errorMessage={errors.customerEmail}
                  isValid={validFields.customerEmail}
                />
                <div className="phone-row">
                  <CountryCodeSelect
                    disabled={isDisabled}
                    label="País"
                    name="customerCountryCode"
                    value={formData.customerCountryCode}
                    onChange={handleChange}
                    errorMessage={errors.customerCountryCode}
                  />
                  <PhoneInputCheckout
                    disabled={isDisabled}
                    label="Telefone"
                    name="customerPhone"
                    className="small"
                    value={formData.customerPhone}
                    onChange={handleChange}
                    errorMessage={errors.customerPhone}
                    isValid={validFields.customerPhone}
                  />
                </div>
              </div>
            </div>

            <div className="payment-type-container">
              <h2>Forma de Pagamento</h2>
              <div className="payment_buttons">
                {planInfo?.payment_methods?.includes('CREDIT_CARD') && (
                  <button
                    type="button"
                    onClick={() => setPaymentType('credit_card')}
                    className={`payment_type_button ${paymentType === 'credit_card' ? 'active' : ''}`}
                  >
                    Cartão de Crédito
                  </button>
                )}
                {planInfo?.payment_methods?.includes('BOLETO') && (
                  <button
                    type="button"
                    onClick={() => setPaymentType('boleto')}
                    className={`payment_type_button ${paymentType === 'boleto' ? 'active' : ''}`}
                  >
                    Boleto
                  </button>
                )}
                {planInfo?.payment_methods?.includes('PIX') && (
                  <button
                    type="button"
                    onClick={() => setPaymentType('pix')}
                    className={`payment_type_button ${paymentType === 'pix' ? 'active' : ''}`}
                  >
                    PIX
                  </button>
                )}
              </div>
            </div>

            {paymentType === 'credit_card' && (
              <div className="payment_form">
                <InputCheckout
                  disabled={isDisabled}
                  label="CPF ou CNPJ"
                  name="taxpayerId"
                  value={formData.taxpayerId}
                  onChange={handleChange}
                  errorMessage={errors.taxpayerId}
                  isValid={validFields.taxpayerId}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="Número do cartão"
                  name="ccNumber"
                  value={formData.ccNumber}
                  onChange={handleChange}
                  errorMessage={errors.ccNumber}
                  isValid={validFields.ccNumber}
                />
                <div className="row">
                  <ExpiryDateSelector
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    onMonthChange={handleMonthChange}
                    onYearChange={handleYearChange}
                  />
                  <InputCheckout
                    disabled={isDisabled}
                    label="CVV"
                    name="ccCvv"
                    className='small'
                    value={formData.ccCvv}
                    maxLength={4}
                    onChange={handleChange}
                    errorMessage={errors.ccCvv}
                    isValid={validFields.ccCvv}
                  />
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="Nome do Portador"
                  name="ccName"
                  value={formData.ccName}
                  onChange={handleChange}
                  errorMessage={errors.ccName}
                  isValid={validFields.ccName}
                />
                <label className="save-card-checkbox">
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    disabled={isDisabled}
                  />
                  <span>Salvar cartão para cobranças futuras</span>
                </label>
              </div>
            )}

            {paymentType === 'boleto' && (
              <div className="payment_form">
                <div className="payment__redirect">
                  O Banco solicita algumas informações extras para a geração do boleto.
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="CPF ou CNPJ"
                  name="taxpayerId"
                  value={formData.taxpayerId}
                  onChange={handleChange}
                  errorMessage={errors.taxpayerId}
                  isValid={validFields.taxpayerId}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="CEP"
                  name="addressCep"
                  value={formData.addressCep}
                  onChange={handleChange}
                  errorMessage={errors.addressCep}
                  isValid={validFields.addressCep}
                  maxLength={9}
                />
                <InputCheckout
                  disabled={isDisabled}
                  label="Endereço"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  errorMessage={errors.addressLine1}
                  isValid={validFields.addressLine1}
                />
                <div className="row">
                  <InputCheckout
                    disabled={isDisabled}
                    label="Número"
                    name="addressNumber"
                    className="small"
                    value={formData.addressNumber}
                    onChange={handleChange}
                    errorMessage={errors.addressNumber}
                    isValid={validFields.addressNumber}
                  />
                  <InputCheckout
                    disabled={isDisabled}
                    label="Complemento"
                    name="addressComplement"
                    className="small"
                    value={formData.addressComplement}
                    onChange={handleChange}
                    isValid={formData.addressComplement ? true : ''}
                  />
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="Bairro"
                  name="addressNeighborhood"
                  value={formData.addressNeighborhood}
                  onChange={handleChange}
                  errorMessage={errors.addressNeighborhood}
                  isValid={validFields.addressNeighborhood}
                />
                <div className="row">
                  <InputCheckout
                    disabled={isDisabled}
                    label="Cidade"
                    name="addressCity"
                    className="small"
                    value={formData.addressCity}
                    onChange={handleChange}
                    errorMessage={errors.addressCity}
                    isValid={validFields.addressCity}
                  />
                  <StateSelect
                    label="Estado"
                    name="addressState"
                    value={formData.addressState}
                    onChange={handleChange}
                    errorMessage={errors.addressState}
                    isValid={validFields.addressState}
                  />
                </div>
              </div>
            )}

            {paymentType === 'pix' && (
              <div className="payment_form">
                <div className="payment__redirect">
                  Ao clicar em "Assinar" você será redirecionado para o QRCode do PIX.
                </div>
                <InputCheckout
                  disabled={isDisabled}
                  label="CPF ou CNPJ"
                  name="taxpayerId"
                  value={formData.taxpayerId}
                  onChange={handleChange}
                  errorMessage={errors.taxpayerId}
                  isValid={validFields.taxpayerId}
                />
              </div>
            )}

            <div className="payment-container">
              {loading ? (
                <div className="loading-div">
                  <span className="loader-text">Processando...</span>
                  <span className="loader"></span>
                </div>
              ) : (
                <button type="submit">ASSINAR</button>
              )}
            </div>
          </form>
        </div>
      )}

      {returnPix && (
        <div className='pix-feedback'>
          <h2>Seu QRCode está pronto!</h2>
          <img src={returnPix.payment.pix.qr_code} alt="QR Code" width={250} />
          <h3>Pix Copia e Cola</h3>
          <div className='pix-copy-paste'>
            <div className='pix-code'>{returnPix.payment.pix.copy_paste}</div>
            <button onClick={handleCopy}>Copiar</button>
          </div>
          <p className="pix-message">
            Após o pagamento, sua assinatura será ativada automaticamente.
          </p>
        </div>
      )}

      {returnBoleto && (
        <div className='boleto-feedback'>
          <h2>Visualizar Boleto</h2>
          <p className="boleto-message">
            Após o pagamento, sua assinatura será ativada automaticamente.
          </p>
          <div>
            <button onClick={() => window.open(returnBoleto?.payment?.boleto?.url, '_blank')}>
              Visualizar página
            </button>
          </div>
          <iframe
            src={returnBoleto?.payment?.boleto?.url}
            width="100%"
            height="600px"
            title="Boleto"
            style={{ border: 'none', marginTop: '24px' }}
          />
        </div>
      )}

      {subscriptionSuccess && <SubscriptionSuccess />}
      {errorPurchase && <OrderUnsuccessful errorDetails={errorDetails} />}
    </>
  );
};

export default SubscriptionCheckoutForm;
